import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { plants } from "@/lib/api";
import { enqueueIdentifyRetry } from "@/lib/offlineCache";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Encapsulates the "identify transiently failed — save the photo as a draft
 * and queue it for background retry" flow that's shared by:
 *   • single-shot camera captures
 *   • bulk file uploads
 *   • multi-angle (2-4 photo) shots
 *
 * Design rules the returned helpers follow:
 *   - Purely `data → data`. UI side-effects (setting snapshot, navigating,
 *     firing toasts) are OPT-IN via the `showToast` flag on
 *     `queueTransientFailure`. Bulk callers pass `showToast: false` and
 *     render one batch-level summary themselves.
 *   - Every function is safe to call from an unmounted component; nothing
 *     touches component state directly.
 *   - Never throws for expected transient errors — returns `null` so callers
 *     can fall through to their own error UX.
 */
export function useIdentifyRetryQueue() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  /** True when an axios error looks like a network / origin-overload blip
   *  and NOT a genuine identification rejection. */
  const isTransientServerFailure = useCallback((err) => {
    if (!err) return false;
    if (!err.response) return true;                              // axios network error / timeout
    const status = err.response.status;
    return (
      status === 408 || status === 429 ||
      status === 502 || status === 503 || status === 504 || status === 524
    );
  }, []);

  /** Sleep helper for retry delays. */
  const sleep = useCallback((ms) => new Promise((r) => setTimeout(r, ms)), []);

  /** Attempt an identify call. On a TRANSIENT failure only, retry once after
   *  `retryDelayMs`. Non-transient errors (400, 401, model rejection) bubble
   *  up immediately so the parent can render a proper error toast. */
  const identifyWithOneRetry = useCallback(async (fn, retryDelayMs = 1500) => {
    try {
      return await fn();
    } catch (e) {
      if (!isTransientServerFailure(e)) throw e;
      await sleep(retryDelayMs);
      return await fn();
    }
  }, [isTransientServerFailure, sleep]);

  /** Persist the compressed photo as a draft + enqueue it in IndexedDB for
   *  the background retry worker (mounted in <App />). Returns
   *  `{ scan_id }` on success, `null` on failure. Emits a friendly toast
   *  only when `showToast` is true. */
  const queueTransientFailure = useCallback(async (
    compressedImage,
    loc,
    { showToast = true, onQueued } = {},
  ) => {
    try {
      const { data: draft } = await plants.saveDraft(compressedImage, loc);
      if (draft?.scan_id && user?.user_id) {
        await enqueueIdentifyRetry(draft.scan_id, user.user_id);
      }
      if (showToast) {
        toast.message(t("camera.toastIdentifyQueued"), {
          description: t("camera.toastIdentifyQueuedDesc"),
          duration: 8000,
          action: {
            label: t("camera.viewInGallery"),
            onClick: () => navigate("/gallery"),
          },
        });
        // Parent side-effect (e.g. setSnapshot(null)) — optional so bulk
        // callers that manage their own UI aren't affected.
        onQueued?.();
      }
      return draft?.scan_id ? { scan_id: draft.scan_id } : null;
    } catch (drafterr) {
      if (process.env.NODE_ENV !== "production") {
        // Kept as console.warn (not console.error) — this is a fallback path
        // and the caller will render an interactive error toast next.
        console.warn("[camera] queue-on-failure fallback:", drafterr);
      }
      return null;
    }
  }, [t, user, navigate]);

  return {
    isTransientServerFailure,
    identifyWithOneRetry,
    queueTransientFailure,
    sleep,
  };
}
